/** AT07 copy (one group per control; a ticket edits only its own file). */
export const at07PtBr = {
  title: 'Créditos de PIS/COFINS do imobilizado',
  description:
    'Concilia os créditos de PIS/COFINS do imobilizado com a mesma conciliação que bloqueia o fechamento: calculado, apropriado, disponível e utilizado.',
  prerequisite: 'Requer a reutilização da conciliação de créditos de PIS/COFINS do imobilizado.',
  scopeNote:
    'Avalia somente créditos de PIS/COFINS de ativo imobilizado. Créditos de ICMS (CIAP) e de ISS não são avaliados por este controle.',
  partialNote:
    'Cobertura parcial: o resultado abaixo é o da conciliação do fechamento, mas o perfil fiscal desta entidade está fora do que ela suporta. O controle não é considerado conforme.',
  creditNote: 'Valores do crédito fiscal, não da depreciação contábil do ativo.',
  positions: {
    title: 'Posição dos créditos',
    tax: { PIS: 'PIS', COFINS: 'COFINS' },
    competence: 'Competência {{competence}}',
    calculated: 'Calculado',
    appropriated: 'Apropriado',
    available: 'Disponível',
    used: 'Utilizado',
    truncated: 'Mostrando {{shown}} de {{total}} posições.',
  },
  findings: {
    title: 'Divergências',
    unknownReason: 'Divergência na conciliação do crédito.',
    openAsset: 'Abrir ativo',
    difference: 'Diferença',
    costDifference: 'Diferença de custo/base',
    direction: {
      credit: {
        above: 'Registrado acima do esperado',
        below: 'Registrado abaixo do esperado',
      },
      balance: {
        above: 'Saldo registrado acima do limite',
        below: 'Saldo registrado abaixo do previsto',
      },
      cost: {
        above: 'Acima do valor de referência',
        below: 'Abaixo do valor de referência',
      },
    },
    truncated: 'Mostrando {{shown}} de {{total}} divergências.',
  },
  observations: {
    AT07_ENTITY_OUTSIDE_BRAZIL_one:
      '{{count}} posição de crédito em entidade fora do Brasil: a conciliação de PIS/COFINS foi feita para entidades brasileiras e não pode ser confirmada aqui.',
    AT07_ENTITY_OUTSIDE_BRAZIL_other:
      '{{count}} posições de crédito em entidade fora do Brasil: a conciliação de PIS/COFINS foi feita para entidades brasileiras e não pode ser confirmada aqui.',
    AT07_REGIME_NOT_NON_CUMULATIVE_one:
      '{{count}} posição de crédito em contribuinte fora do regime não cumulativo de PIS/COFINS, com saldo trazido de período anterior ou contribuintes da entidade em regimes diferentes: a transição de regime não é avaliada por este controle.',
    AT07_REGIME_NOT_NON_CUMULATIVE_other:
      '{{count}} posições de crédito em contribuinte fora do regime não cumulativo de PIS/COFINS, com saldo trazido de período anterior ou contribuintes da entidade em regimes diferentes: a transição de regime não é avaliada por este controle.',
    AT07_TAX_PROFILE_UNRESOLVED_one:
      '{{count}} posição de crédito sem perfil fiscal comprovado no período: o histórico fiscal do contribuinte está ausente, sobreposto ou muda no meio do período.',
    AT07_TAX_PROFILE_UNRESOLVED_other:
      '{{count}} posições de crédito sem perfil fiscal comprovado no período: o histórico fiscal do contribuinte está ausente, sobreposto ou muda no meio do período.',
    AT07_RESTRICTED_ACCOUNT_FINDINGS_one:
      '{{count}} divergência em conta com acesso restrito, contada sem detalhes: peça a quem tem acesso a essa conta para conferir o crédito.',
    AT07_RESTRICTED_ACCOUNT_FINDINGS_other:
      '{{count}} divergências em contas com acesso restrito, contadas sem detalhes: peça a quem tem acesso a essas contas para conferir os créditos.',
  },
  reasons: {
    EVENT_ALLOCATION_MISMATCH: 'O valor do evento de crédito não corresponde às suas alocações.',
    EVENT_IDENTITY_OR_TYPE_INVALID: 'O evento de crédito tem identificação ou tipo inválido.',
    INVALID_SUBLEDGER_ONLY_EVENT:
      'Evento de crédito registrado só no subledger, sem o lançamento contábil que ele exige.',
    JOURNAL_MISSING_OR_NOT_POSTED:
      'O lançamento contábil do evento de crédito não existe ou não está contabilizado.',
    JOURNAL_SCOPE_MISMATCH:
      'O lançamento do evento de crédito pertence a outra entidade, outro livro ou outro período.',
    JOURNAL_EVENT_AMOUNT_MISMATCH: 'O valor lançado no razão difere do valor do evento de crédito.',
    JOURNAL_LINE_MISSING_OR_DIVERGENT:
      'Falta uma linha do lançamento do crédito, ou ela diverge do evento.',
    JOURNAL_LINE_CURRENCY_BASIS_INVALID:
      'A base de moeda de uma linha do lançamento do crédito é inválida.',
    FA_PIS_COFINS_RECOVERABILITY_LOSS_REQUEST_INVALID:
      'A solicitação de perda de recuperabilidade do crédito é inválida.',
    FA_PIS_COFINS_RECOVERABILITY_LOSS_BALANCE_INVALID:
      'Os saldos do crédito (potencial, apropriado, disponível, utilizado e provisão) não fecham entre si.',
    FIXED_ASSET_DISPOSAL_PIS_COFINS_FUTURE_MATURATION_CONFLICT:
      'A baixa do ativo conflita com parcelas do crédito que ainda vão vencer.',
    FIXED_ASSET_DISPOSAL_PIS_COFINS_POST_2026_UNSUPPORTED:
      'Baixa de ativo com crédito de PIS/COFINS depois de 2026 ainda não é suportada.',
    AVAILABLE_OWNER_BALANCE_INVALID:
      'O saldo disponível do crédito diverge do que o razão atribui ao ativo.',
    COMMON_USE_SOURCE_SCOPE_INVALID:
      'Exclusão por uso comum registrada em outra entidade, livro, moeda ou lançamento de origem.',
    COMMON_USE_TARGET_BALANCE_INVALID:
      'O valor excluído por uso comum não fecha com as parcelas do crédito.',
    COMMON_USE_TARGET_MISSING: 'Falta o ativo de destino da exclusão por uso comum.',
    COMMON_USE_OWNER_POSTING_MISSING: 'Exclusão por uso comum sem o lançamento contábil no livro.',
    DEFERRED_OWNER_BALANCE_INVALID:
      'O saldo diferido do crédito diverge do que o razão atribui ao ativo.',
    ENTITLEMENT_ACCOUNTING_OWNER_INVALID:
      'O direito ao crédito não está atribuído ao ativo correto na contabilidade.',
    ENTITLEMENT_PROVENANCE_INVALID: 'A origem do direito ao crédito não pôde ser comprovada.',
    ENTITLEMENT_RELEASE_LINEAGE_INVALID:
      'A cadeia de liberação do direito ao crédito está inconsistente.',
    EVENT_SCOPE_INVALID:
      'Evento de crédito registrado fora da entidade ou do livro deste fechamento.',
    SOURCE_JOURNAL_INVALID:
      'O lançamento de origem do crédito é inválido: ausente, não contabilizado ou de outro escopo.',
    COMMON_USE_BOOK_REVISION_HISTORY_INVALID:
      'O histórico de revisões do livro para uso comum está inconsistente.',
    COMMON_USE_BOOK_REVISION_OWNER_INVALID:
      'A revisão do livro para uso comum aponta para um responsável inválido.',
    COMMON_USE_BOOK_REVISION_SCHEDULE_LINEAGE_INVALID:
      'A revisão do livro para uso comum não se liga ao cronograma esperado.',
    COMMON_USE_BOOK_REVISION_SCOPE_INVALID:
      'A revisão do livro para uso comum pertence a outro escopo.',
    COMMON_USE_OWNER_BOOK_SCOPE_INVALID: 'O responsável pelo uso comum pertence a outro livro.',
    COMMON_USE_OWNER_COST_DIVERGENT: 'O custo do responsável pelo uso comum diverge do esperado.',
    COMMON_USE_OWNER_DISPOSAL_LINEAGE_INVALID:
      'A baixa do responsável pelo uso comum não se liga ao histórico esperado.',
    COMMON_USE_OWNER_HISTORY_INVALID:
      'O histórico do responsável pelo uso comum está inconsistente.',
    COMMON_USE_OWNER_PLAN_VERSION_INVALID:
      'A versão do plano do responsável pelo uso comum é inválida.',
    COMMON_USE_OWNER_SCHEDULE_DIVERGENT:
      'O cronograma do responsável pelo uso comum diverge do esperado.',
    COMMON_USE_OWNER_SCHEDULE_LINEAGE_INVALID:
      'O cronograma do responsável pelo uso comum não se liga ao histórico esperado.',
    AT07_CREDIT_APPROPRIATED_UNDER_CUMULATIVE_REGIME:
      'Crédito apropriado no período com o contribuinte no regime cumulativo de PIS/COFINS: crédito de imobilizado só existe no regime não cumulativo (Lei 10.637/2002, art. 8º; Lei 10.833/2003, art. 10).',
  },
};

export const at07En: typeof at07PtBr = {
  title: 'Fixed asset PIS/COFINS credits',
  description:
    'Reconciles fixed asset PIS/COFINS credits with the same reconciliation that blocks the close: calculated, appropriated, available and used.',
  prerequisite: 'Needs the fixed asset PIS/COFINS credit reconciliation to be reused.',
  scopeNote:
    'Evaluates fixed asset PIS/COFINS credits only. ICMS (CIAP) and ISS credits are not evaluated by this control.',
  partialNote:
    "Partial coverage: the result below is the close's own reconciliation, but this entity's tax profile is outside what it supports. The control is not treated as conforming.",
  creditNote: "Tax credit amounts, not the asset's book depreciation.",
  positions: {
    title: 'Credit positions',
    tax: { PIS: 'PIS', COFINS: 'COFINS' },
    competence: 'Competence {{competence}}',
    calculated: 'Calculated',
    appropriated: 'Appropriated',
    available: 'Available',
    used: 'Used',
    truncated: 'Showing {{shown}} of {{total}} positions.',
  },
  findings: {
    title: 'Differences',
    unknownReason: 'Credit reconciliation difference.',
    openAsset: 'Open asset',
    difference: 'Difference',
    costDifference: 'Cost/base difference',
    direction: {
      credit: {
        above: 'Recorded above the expected amount',
        below: 'Recorded below the expected amount',
      },
      balance: {
        above: 'Balance recorded above its limit',
        below: 'Balance recorded below the expected amount',
      },
      cost: {
        above: 'Above the reference amount',
        below: 'Below the reference amount',
      },
    },
    truncated: 'Showing {{shown}} of {{total}} differences.',
  },
  observations: {
    AT07_ENTITY_OUTSIDE_BRAZIL_one:
      '{{count}} credit position in an entity outside Brazil: the PIS/COFINS reconciliation was built for Brazilian entities and cannot be confirmed here.',
    AT07_ENTITY_OUTSIDE_BRAZIL_other:
      '{{count}} credit positions in an entity outside Brazil: the PIS/COFINS reconciliation was built for Brazilian entities and cannot be confirmed here.',
    AT07_REGIME_NOT_NON_CUMULATIVE_one:
      "{{count}} credit position for a taxpayer outside the non-cumulative PIS/COFINS regime, with a balance carried from an earlier period or the entity's taxpayers on different regimes: this control does not evaluate the regime transition.",
    AT07_REGIME_NOT_NON_CUMULATIVE_other:
      "{{count}} credit positions for a taxpayer outside the non-cumulative PIS/COFINS regime, with a balance carried from an earlier period or the entity's taxpayers on different regimes: this control does not evaluate the regime transition.",
    AT07_TAX_PROFILE_UNRESOLVED_one:
      "{{count}} credit position without a proven tax profile for the period: the taxpayer's fiscal history is missing, overlapping or changes mid-period.",
    AT07_TAX_PROFILE_UNRESOLVED_other:
      "{{count}} credit positions without a proven tax profile for the period: the taxpayer's fiscal history is missing, overlapping or changes mid-period.",
    AT07_RESTRICTED_ACCOUNT_FINDINGS_one:
      '{{count}} difference on a restricted-access account, counted without details: ask someone with access to that account to check the credit.',
    AT07_RESTRICTED_ACCOUNT_FINDINGS_other:
      '{{count}} differences on restricted-access accounts, counted without details: ask someone with access to those accounts to check the credits.',
  },
  reasons: {
    EVENT_ALLOCATION_MISMATCH: "The credit event amount does not match the event's allocations.",
    EVENT_IDENTITY_OR_TYPE_INVALID: 'The credit event has an invalid identity or type.',
    INVALID_SUBLEDGER_ONLY_EVENT:
      'Credit event recorded only in the subledger, without the journal it requires.',
    JOURNAL_MISSING_OR_NOT_POSTED: "The credit event's journal does not exist or is not posted.",
    JOURNAL_SCOPE_MISMATCH: "The credit event's journal belongs to another entity, book or period.",
    JOURNAL_EVENT_AMOUNT_MISMATCH:
      'The amount posted in the ledger differs from the credit event amount.',
    JOURNAL_LINE_MISSING_OR_DIVERGENT:
      'A line of the credit journal is missing or differs from the event.',
    JOURNAL_LINE_CURRENCY_BASIS_INVALID: 'The currency basis of a credit journal line is invalid.',
    FA_PIS_COFINS_RECOVERABILITY_LOSS_REQUEST_INVALID:
      'The credit recoverability loss request is invalid.',
    FA_PIS_COFINS_RECOVERABILITY_LOSS_BALANCE_INVALID:
      'The credit balances (potential, appropriated, available, used and allowance) do not add up.',
    FIXED_ASSET_DISPOSAL_PIS_COFINS_FUTURE_MATURATION_CONFLICT:
      'The asset disposal conflicts with credit installments that have not matured yet.',
    FIXED_ASSET_DISPOSAL_PIS_COFINS_POST_2026_UNSUPPORTED:
      'Disposing of an asset with PIS/COFINS credit after 2026 is not supported yet.',
    AVAILABLE_OWNER_BALANCE_INVALID:
      'The available credit balance differs from what the ledger assigns to the asset.',
    COMMON_USE_SOURCE_SCOPE_INVALID:
      'Common-use exclusion recorded under another entity, book, currency or source journal.',
    COMMON_USE_TARGET_BALANCE_INVALID:
      'The amount excluded for common use does not add up with the credit installments.',
    COMMON_USE_TARGET_MISSING: 'The target asset of the common-use exclusion is missing.',
    COMMON_USE_OWNER_POSTING_MISSING: 'Common-use exclusion without its journal in the book.',
    DEFERRED_OWNER_BALANCE_INVALID:
      'The deferred credit balance differs from what the ledger assigns to the asset.',
    ENTITLEMENT_ACCOUNTING_OWNER_INVALID:
      'The credit entitlement is not assigned to the right asset in the accounting.',
    ENTITLEMENT_PROVENANCE_INVALID: 'The origin of the credit entitlement could not be proven.',
    ENTITLEMENT_RELEASE_LINEAGE_INVALID: "The credit entitlement's release chain is inconsistent.",
    EVENT_SCOPE_INVALID: "Credit event recorded outside this close's entity or book.",
    SOURCE_JOURNAL_INVALID:
      "The credit's source journal is invalid: missing, not posted or in another scope.",
    COMMON_USE_BOOK_REVISION_HISTORY_INVALID:
      'The common-use book revision history is inconsistent.',
    COMMON_USE_BOOK_REVISION_OWNER_INVALID:
      'The common-use book revision points to an invalid owner.',
    COMMON_USE_BOOK_REVISION_SCHEDULE_LINEAGE_INVALID:
      'The common-use book revision is not linked to the expected schedule.',
    COMMON_USE_BOOK_REVISION_SCOPE_INVALID:
      'The common-use book revision belongs to another scope.',
    COMMON_USE_OWNER_BOOK_SCOPE_INVALID: 'The common-use owner belongs to another book.',
    COMMON_USE_OWNER_COST_DIVERGENT: "The common-use owner's cost differs from what is expected.",
    COMMON_USE_OWNER_DISPOSAL_LINEAGE_INVALID:
      "The common-use owner's disposal is not linked to the expected history.",
    COMMON_USE_OWNER_HISTORY_INVALID: "The common-use owner's history is inconsistent.",
    COMMON_USE_OWNER_PLAN_VERSION_INVALID: "The common-use owner's plan version is invalid.",
    COMMON_USE_OWNER_SCHEDULE_DIVERGENT:
      "The common-use owner's schedule differs from what is expected.",
    COMMON_USE_OWNER_SCHEDULE_LINEAGE_INVALID:
      "The common-use owner's schedule is not linked to the expected history.",
    AT07_CREDIT_APPROPRIATED_UNDER_CUMULATIVE_REGIME:
      'Credit appropriated in the period while the taxpayer was on the cumulative PIS/COFINS regime: fixed asset credits exist only under the non-cumulative regime (Law 10,637/2002, art. 8; Law 10,833/2003, art. 10).',
  },
};
